import { useState, useMemo } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, ArrowRight, ArrowLeft, Search, List } from "lucide-react";

const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return `$${n.toFixed(2)}`;
};
const fmtPct = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(1)}%`;
};
const fmtInt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return Math.round(n).toLocaleString();
};
const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString();
};

function Section({ title, count, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className="box overflow-hidden"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5 text-left hover:bg-[#f2f3ef] transition-colors"
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? (
            <ChevronDown className="w-4 h-4 text-[#0d0d0d] shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#0d0d0d] shrink-0" />
          )}
          <h2 className="font-['Archivo'] font-extrabold text-lg sm:text-xl text-[#0d0d0d] truncate">{title}</h2>
        </div>
        {count !== null && count !== undefined && (
          <span
            className="tag border-2 border-[#0d0d0d] text-[#0d0d0d] bg-transparent min-w-[36px] justify-center"
            data-testid={`${testId}-count`}
          >
            {fmtNum(count)}
          </span>
        )}
      </button>
      {open && <div className="animate-slide-down border-t-2 border-[#0d0d0d]">{children}</div>}
    </section>
  );
}

function MetricCard({ label, value, testId, highlight = false }) {
  return (
    <div className="border-t-2 border-white pt-3" data-testid={testId}>
      <div className="font-brand-mono text-[11px] uppercase tracking-[1.5px] text-[#96a09a]">{label}</div>
      <div className={`display text-3xl sm:text-4xl mt-2 break-words ${highlight ? "text-[#00c389]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Badge({ tone, children, testId }) {
  const tones = {
    red: "bg-[#0d0d0d] text-white",
    amber: "bg-[#e8a13c] text-[#0d0d0d]",
    grey: "bg-transparent border-2 border-[#0d0d0d] text-[#0d0d0d]",
  };
  return (
    <span
      className={`tag whitespace-nowrap ${tones[tone] || tones.grey}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

export default function Step2Preview({ apiBaseUrl, jobId, result, onGenerated, onError, onBack }) {
  const [generating, setGenerating] = useState(false);

  // Normalise API response shape.
  // Real API returns: { job_id, account, summary, negatives[], manual_review[], harvest_terms[], bid_adjustments_sample[] }
  const account = result?.account || result?.account_summary || {};
  const summary = result?.summary || {};

  // Merge negatives (red) + manual_review (amber) into a single table with tagged status.
  const rawNegatives = Array.isArray(result?.negatives) ? result.negatives : [];
  const rawManualReview = Array.isArray(result?.manual_review) ? result.manual_review : [];
  const negRows = [
    ...rawNegatives.map((r) => ({ ...r, _status: "negate" })),
    ...rawManualReview.map((r) => ({ ...r, _status: "review" })),
  ];
  const negCount = summary.negatives_count !== undefined || summary.manual_review_count !== undefined
    ? (summary.negatives_count || 0) + (summary.manual_review_count || 0)
    : negRows.length;
  const spendFreed = summary.negatives_spend_freed ?? result?.total_spend_freed;

  // New campaigns (harvest terms)
  const newCampaigns = Array.isArray(result?.harvest_terms)
    ? result.harvest_terms
    : Array.isArray(result?.new_campaigns)
    ? result.new_campaigns
    : [];
  const newCampaignsCount = summary.new_campaigns_count ?? newCampaigns.length;

  // Bid adjustments (sample may be truncated — use summary counts for the count badge + change line)
  const bidSample = Array.isArray(result?.bid_adjustments_sample)
    ? result.bid_adjustments_sample
    : Array.isArray(result?.bid_adjustments)
    ? result.bid_adjustments
    : [];
  const bidTotal = summary.bid_adjustments_total ?? bidSample.length;
  const increasing = summary.bid_increases ?? bidSample.filter((b) => (b.change_pct ?? 0) > 0).length;
  const decreasing = summary.bid_decreases ?? bidSample.filter((b) => (b.change_pct ?? 0) < 0).length;

  // "See all" — lazy-loaded full list from /adjustments/{job_id}
  const [fullBidItems, setFullBidItems] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [bidSearch, setBidSearch] = useState("");
  const showingFull = fullBidItems !== null;
  const bidItems = showingFull ? fullBidItems : bidSample;
  const isSampleTruncated = !showingFull && bidTotal > bidSample.length;

  const filteredBidItems = useMemo(() => {
    if (!showingFull || !bidSearch.trim()) return bidItems;
    const q = bidSearch.trim().toLowerCase();
    return bidItems.filter((r) => {
      const label = (r.label || r.keyword || r.target || r.search_term || "").toLowerCase();
      const camp = (r.campaign || "").toLowerCase();
      return label.includes(q) || camp.includes(q);
    });
  }, [bidItems, bidSearch, showingFull]);

  const handleSeeAll = async () => {
    if (!jobId) return;
    setLoadingFull(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/adjustments/${jobId}`, { timeout: 60000 });
      const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setFullBidItems(items);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not load the full bid adjustments list.";
      onError(msg);
    } finally {
      setLoadingFull(false);
    }
  };

  const handleCollapseToSample = () => {
    setFullBidItems(null);
    setBidSearch("");
  };

  const handleGenerate = async () => {
    if (!jobId) {
      onError("Missing job ID. Please re-run the analysis.");
      return;
    }
    onError("");
    setGenerating(true);
    try {
      // The real API generates the bulk file during /process and serves it from /download/{job_id}.
      // We don't need a separate generate call — just advance to Step 3.
      // (If a /generate endpoint exists we'll call it; 404 is tolerated.)
      await axios
        .post(`${apiBaseUrl}/generate/${jobId}`, {}, { timeout: 60000 })
        .catch((err) => {
          if (err?.response?.status && err.response.status !== 404) throw err;
        });
      onGenerated();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not generate the bulk file. Please try again.";
      onError(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="step2-root">
      {/* Back link */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 font-brand-mono text-[11px] uppercase tracking-[1.5px] text-[#565b57] hover:text-[#007d62] transition-colors"
        data-testid="back-to-step1"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to upload
      </button>

      {/* Account Summary — dark ink results panel, always expanded */}
      <section
        className="terminal border-l-4 border-l-[#00c389] p-6 sm:p-9 shadow-hard-sm"
        data-testid="section-account-summary"
      >
        <p className="kicker kicker-dark mb-3">Your Numbers</p>
        <h2 className="display text-2xl sm:text-3xl text-white mb-8">Account Summary</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
          <MetricCard label="Total Spend" value={fmtCurrency(account.total_spend)} testId="metric-total-spend" />
          <MetricCard label="Total Sales" value={fmtCurrency(account.total_sales)} testId="metric-total-sales" />
          <MetricCard label="Actual ACoS" value={fmtPct(account.actual_acos)} testId="metric-actual-acos" highlight />
          <MetricCard label="Target ACoS" value={fmtPct(account.target_acos)} testId="metric-target-acos" />
          <MetricCard label="Breakeven ACoS" value={fmtPct(account.breakeven_acos)} testId="metric-breakeven-acos" />
          <MetricCard label="Bid Method" value={account.bid_method || "—"} testId="metric-bid-method" />
          <MetricCard
            label="Bid Cap"
            value={account.cap_pct !== undefined ? fmtPct(account.cap_pct) : fmtCurrency(account.bid_cap)}
            testId="metric-bid-cap"
          />
        </div>
      </section>

      {/* Negatives + Manual Review */}
      <Section
        title="Negative Keywords / Targets"
        count={negCount}
        testId="section-negatives"
      >
        {spendFreed !== undefined && spendFreed !== null && (
          <div
            className="px-5 sm:px-7 py-3.5 bg-[#e2efe9] border-b-2 border-[#0d0d0d] text-sm text-[#0d0d0d] font-medium"
            data-testid="spend-freed-bar"
          >
            Total spend freed: <span className="font-brand-mono font-semibold text-[#007d62]">{fmtCurrency(spendFreed)}</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f2f3ef] text-left border-b-2 border-[#0d0d0d]">
              <tr className="font-brand-mono text-[11px] uppercase tracking-[1.5px] text-[#565b57]">
                <th className="px-4 sm:px-7 py-3.5 font-semibold">Search Term</th>
                <th className="px-4 py-3.5 font-semibold">Type</th>
                <th className="px-4 py-3.5 font-semibold text-right">Spend</th>
                <th className="px-4 py-3.5 font-semibold text-right">Clicks</th>
                <th className="px-4 py-3.5 font-semibold text-right">Orders</th>
                <th className="px-4 sm:px-7 py-3.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {negRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-[#565b57] text-sm">
                    No negative keywords suggested.
                  </td>
                </tr>
              ) : (
                negRows.map((row, i) => (
                  <tr key={i} className="border-t border-[#0d0d0d]/15 hover:bg-[#e2efe9]/40 transition-colors" data-testid={`negatives-row-${i}`}>
                    <td className="px-4 sm:px-7 py-3 text-[#0d0d0d] break-all">{row.search_term || "—"}</td>
                    <td className="px-4 py-3 text-[#565b57]">{row.type || row.entity || "—"}</td>
                    <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtCurrency(row.spend)}</td>
                    <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtInt(row.clicks)}</td>
                    <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtInt(row.orders)}</td>
                    <td className="px-4 sm:px-7 py-3">
                      {row._status === "negate" ? (
                        <Badge tone="red">Will be negated</Badge>
                      ) : (
                        <Badge tone="amber">Manual review</Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* New Campaigns (harvest terms) */}
      <Section
        title="New Exact Match Campaigns"
        count={newCampaignsCount}
        testId="section-new-campaigns"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f2f3ef] text-left border-b-2 border-[#0d0d0d]">
              <tr className="font-brand-mono text-[11px] uppercase tracking-[1.5px] text-[#565b57]">
                <th className="px-4 sm:px-7 py-3.5 font-semibold">Search Term</th>
                <th className="px-4 py-3.5 font-semibold">Type</th>
                <th className="px-4 py-3.5 font-semibold text-right">Sales</th>
                <th className="px-4 py-3.5 font-semibold text-right">Orders</th>
                <th className="px-4 py-3.5 font-semibold text-right">Opening Bid</th>
                <th className="px-4 sm:px-7 py-3.5 font-semibold">Campaign Name</th>
              </tr>
            </thead>
            <tbody>
              {newCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-[#565b57] text-sm">
                    No new campaigns suggested.
                  </td>
                </tr>
              ) : (
                newCampaigns.map((row, i) => (
                  <tr key={i} className="border-t border-[#0d0d0d]/15 hover:bg-[#e2efe9]/40 transition-colors" data-testid={`new-campaign-row-${i}`}>
                    <td className="px-4 sm:px-7 py-3 text-[#0d0d0d] break-all">{row.search_term || "—"}</td>
                    <td className="px-4 py-3 text-[#565b57]">{row.type || "Keyword"}</td>
                    <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtCurrency(row.sales)}</td>
                    <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtInt(row.orders)}</td>
                    <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtCurrency(row.opening_bid)}</td>
                    <td className="px-4 sm:px-7 py-3 text-[#0d0d0d] text-xs break-all">{row.campaign_name || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Bid Adjustments */}
      <Section
        title="Bid Adjustments"
        count={bidTotal}
        testId="section-bid-adjustments"
      >
        <div
          className="px-5 sm:px-7 py-3.5 bg-[#f2f3ef] border-b-2 border-[#0d0d0d] text-sm text-[#565b57] flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
          data-testid="bid-summary-line"
        >
          <span>
            <span className="font-brand-mono font-semibold text-[#007d62]">{fmtNum(increasing)}</span> bids increasing ·{" "}
            <span className="font-brand-mono font-semibold text-[#d6453d]">{fmtNum(decreasing)}</span> bids decreasing
            {isSampleTruncated && (
              <span className="font-brand-mono text-[10px] uppercase tracking-wider text-[#96a09a] ml-2">
                (showing first {bidItems.length} of {fmtNum(bidTotal)})
              </span>
            )}
            {showingFull && (
              <span className="font-brand-mono text-[10px] uppercase tracking-wider text-[#96a09a] ml-2">
                (showing all {fmtNum(bidTotal)}
                {bidSearch && filteredBidItems.length !== bidItems.length
                  ? ` · ${fmtNum(filteredBidItems.length)} match`
                  : ""}
                )
              </span>
            )}
          </span>
          <div className="flex items-center gap-3">
            {isSampleTruncated && (
              <button
                type="button"
                onClick={handleSeeAll}
                disabled={loadingFull}
                className="inline-flex items-center gap-1.5 font-brand-mono text-[11px] uppercase tracking-[1px] font-semibold text-[#007d62] hover:text-[#0d0d0d] disabled:text-[#96a09a] disabled:cursor-not-allowed transition-colors"
                data-testid="see-all-bids-btn"
              >
                <List className="w-3.5 h-3.5" />
                {loadingFull ? "Loading…" : `See all ${fmtNum(bidTotal)}`}
              </button>
            )}
            {showingFull && (
              <button
                type="button"
                onClick={handleCollapseToSample}
                className="font-brand-mono text-[11px] uppercase tracking-[1px] font-semibold text-[#565b57] hover:text-[#007d62] transition-colors"
                data-testid="collapse-bids-btn"
              >
                Show sample only
              </button>
            )}
          </div>
        </div>

        {showingFull && (
          <div className="px-5 sm:px-7 py-3.5 border-b-2 border-[#0d0d0d] bg-white" data-testid="bid-search-wrapper">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 text-[#96a09a] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={bidSearch}
                onChange={(e) => setBidSearch(e.target.value)}
                placeholder="Filter by keyword or campaign…"
                className="input-sharp !pl-9 !py-2 font-brand-mono !text-[13px]"
                data-testid="bid-search-input"
              />
            </div>
          </div>
        )}

        <div
          className={showingFull ? "overflow-auto max-h-[520px]" : "overflow-x-auto"}
          data-testid="bid-table-scroll"
        >
          <table className="w-full text-sm">
            <thead className={`bg-[#f2f3ef] text-left border-b-2 border-[#0d0d0d] ${showingFull ? "sticky top-0 z-10" : ""}`}>
              <tr className="font-brand-mono text-[11px] uppercase tracking-[1.5px] text-[#565b57]">
                <th className="px-4 sm:px-7 py-3.5 font-semibold">Keyword / Target</th>
                <th className="px-4 py-3.5 font-semibold">Type</th>
                <th className="px-4 py-3.5 font-semibold text-right">Current Bid</th>
                <th className="px-4 py-3.5 font-semibold text-right">Target CPC</th>
                <th className="px-4 py-3.5 font-semibold text-right">New Bid</th>
                <th className="px-4 sm:px-7 py-3.5 font-semibold text-right">Change %</th>
              </tr>
            </thead>
            <tbody>
              {filteredBidItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-[#565b57] text-sm">
                    {showingFull && bidSearch ? "No bids match your search." : "No bid adjustments suggested."}
                  </td>
                </tr>
              ) : (
                filteredBidItems.map((row, i) => {
                  const changeRaw = row.change_pct ?? row.change ?? 0;
                  const change = typeof changeRaw === "number" ? changeRaw : parseFloat(changeRaw);
                  const isUp = change > 0;
                  const isDown = change < 0;
                  const label = row.label || row.keyword || row.target || row.search_term || "—";
                  const type = row.entity_type || row.type || row.match_type || "—";
                  return (
                    <tr key={i} className="border-t border-[#0d0d0d]/15 hover:bg-[#e2efe9]/40 transition-colors" data-testid={`bid-row-${i}`}>
                      <td className="px-4 sm:px-7 py-3 text-[#0d0d0d] break-all">{label}</td>
                      <td className="px-4 py-3 text-[#565b57]">{type}</td>
                      <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtCurrency(row.current_bid)}</td>
                      <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d]">{fmtCurrency(row.target_cpc)}</td>
                      <td className="px-4 py-3 text-right font-brand-mono text-[13px] text-[#0d0d0d] font-semibold">{fmtCurrency(row.new_bid)}</td>
                      <td
                        className={[
                          "px-4 sm:px-7 py-3 text-right font-brand-mono text-[13px] font-semibold",
                          isUp && "text-[#007d62]",
                          isDown && "text-[#d6453d]",
                          !isUp && !isDown && "text-[#96a09a]",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {Number.isFinite(change) ? `${change > 0 ? "+" : ""}${change.toFixed(1)}%` : "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Generate button */}
      <div className="flex flex-col items-center pt-6 gap-4">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="btn-primary"
          data-testid="generate-bulk-btn"
        >
          {generating ? "Preparing…" : "Generate Bulk Upload File"}
          {!generating && <ArrowRight className="w-4 h-4" />}
        </button>
        <p className="font-brand-mono text-[10px] uppercase tracking-[1px] text-[#96a09a] text-center max-w-xl leading-relaxed">
          Reviewing these changes before uploading is your responsibility. This tool does not make changes directly to your account.
        </p>
      </div>
    </div>
  );
}
